import {
  combineStrings,
  formatDateShort,
  formatPhoneNumber,
  isPresent,
  toTitleCase,
} from 'Shared/utils';
import { transformAddressOutcomes } from '../PostStartStep/response';
import {
  DriversDataType,
  DriverType,
  GetDetailsResponseType,
  InsurableItemType,
  InsurablePartyType,
  InsuranceProductType,
  ServerResponseType,
  ClientFieldDataType,
  VehiclesDataType,
  FieldDataType,
  AddressType,
  DriverCommandsType,
  AgencyInformationType,
  ServerErrorResponseType,
  VehicleCommandsType,
  CommandType,
  AddressCategoryType,
  AddressDataType,
  PriorPolicyResponseType,
} from './types';
import { ISOMaskedRegex, parseFromMaskedISO } from 'QuoteApp/utils';

type InsuranceProductPolicyHoldersType = ServerResponseType['insuranceApplication']['attributes']['insuranceProducts'][0]['attributes']['policyHolders'];

class ProductToggleableError extends Error {
  productToggled: boolean;

  constructor(message: string, productToggled: boolean) {
    super(message);
    this.productToggled = productToggled;
  }
}

const DEFAULTS = {
  vehicle: {
    activeDisable: 'false',
    alarm: 'false',
    passiveDisable: 'false',
    recovery: 'false',
  },
};

const getDriverType = (
  insuranceProductPolicyHolders: InsuranceProductPolicyHoldersType,
  driverId: string
): DriverType => {
  return (
    insuranceProductPolicyHolders.find(
      policyHolder => policyHolder.identifier === driverId
    )?.policyHolderType.value ?? 'principalInsured'
  );
};

export const getPrincipalInsured = (serverResponse: ServerResponseType) => {
  const policyHolders = getInsurableProductPolicyHolders(serverResponse);
  return serverResponse.insuranceApplication.attributes.insurableParties.find(
    driver =>
      getDriverType(policyHolders, driver.attributes.identifier) ===
      'principalInsured'
  );
};

export const isInsuranceApplication = (
  value: ServerResponseType | ServerErrorResponseType
): value is ServerResponseType => {
  return value.hasOwnProperty('insuranceApplication');
};

export const convertToString = (value: boolean | null): string => {
  return value !== null ? value.toString() : '';
};

export const transformAppDetails = (
  serverResponse: ServerResponseType | ServerErrorResponseType
): GetDetailsResponseType => {
  if (isInsuranceApplication(serverResponse)) {
    const autoInsuranceProduct = getAutoInsuranceProduct(serverResponse);

    return {
      drivers: transformDriverDetails(serverResponse),
      vehicles: transformVehicleDetails(serverResponse),
      contactInfo: transformContactInfo(serverResponse),
      coverages: transformCoverageDetails(serverResponse),
      vehicleDriverAssignment: transformVehicleDriverAssigments(serverResponse),
      commands: transformCommands(serverResponse),
      address: transformAddressDetails(serverResponse, 'residency'),
      addressOutcomes: transformAddressOutcomes(serverResponse, 'residency'),
      mailingAddress: transformAddressDetails(serverResponse, 'mailing'),
      mailingAddressOutcomes: transformAddressOutcomes(
        serverResponse,
        'mailing'
      ),
      effectiveDate: transformEffectiveDate(serverResponse, 'effectiveDate'),
      terminationDate: transformEffectiveDate(
        serverResponse,
        'terminationDate'
      ),
      underwriting: transformUnderwritingDetails(serverResponse),
      underwritingConsent: transformUnderwritingConsent(serverResponse),
      quoteNumber: transformQuoteNumber(serverResponse),
      status: transformStatus(serverResponse),
      principalInsuredId:
        getPrincipalInsured(serverResponse)?.attributes.identifier ?? '',
      principalInsuredName: toTitleCase(
        combineStrings([
          getPrincipalInsured(serverResponse)?.attributes.personalInfo
            .attributes.firstName.value ?? '',
          getPrincipalInsured(serverResponse)?.attributes.personalInfo
            .attributes.middleName.value ?? '',
          getPrincipalInsured(serverResponse)?.attributes.personalInfo
            .attributes.lastName.value ?? '',
        ])
      ),
      eligibleDiscounts:
        autoInsuranceProduct &&
        transformEligibleDiscounts(autoInsuranceProduct),
      agencyInformation:
        autoInsuranceProduct &&
        transformAgencyInformation(autoInsuranceProduct),
      documentSignatures: transformDocumentSignatures(serverResponse),
      softStops: transformSoftStops(serverResponse),
      disclosures: transformDisclosures(serverResponse),
      quoteChangeAcknowledgement:
        serverResponse.insuranceApplication.attributes
          .quoteChangeAcknowledgement,
      paymentPreferences:
        serverResponse.insuranceApplication.attributes.paymentPreferences,
      flexiblePaymentPreferences:
        serverResponse.insuranceApplication.attributes
          .flexiblePaymentPreferences,
      configDiscoveryParams:
        serverResponse.insuranceApplication.attributes.configDiscoveryParams,
      productToggled: serverResponse.insuranceApplication.productToggled,
      policyHolders: transformPolicyHolders(autoInsuranceProduct),
      productType: transformProductType(autoInsuranceProduct),
      telematics: transformTelematics(serverResponse),
      productSegment: transformProductSegment(autoInsuranceProduct),
      priorPolicies: transformPriorPolicies(serverResponse),
    };
  } else {
    // server returned an error, so we throw an error and the consumer handles these errors.
    throw new ProductToggleableError(
      serverResponse.message,
      !!serverResponse.public_context?.product_toggled
    );
  }
};

export const transformVehicleDriverAssigments = (
  serverResponse: ServerResponseType
) => {
  const {
    insurableItems,
    insurableParties,
  } = serverResponse.insuranceApplication.attributes;
  const address = transformAddressDetails(serverResponse, 'residency');
  const insuranceProduct = getAutoInsuranceProduct(serverResponse);
  const principalInsured = getPrincipalInsured(serverResponse);

  if (
    address[principalInsured!.attributes.identifier].state.value !== 'VA' ||
    insurableItems.length <= 1 ||
    insurableParties.length <= 1
  ) {
    return null;
  }

  return insuranceProduct!.attributes.insurableItemParties.elements.reduce(
    (acc, element) => ({
      ...acc,
      [element.insurableItem]: element.insurableParties,
    }),
    {}
  );
};

export const transformContactInfo = (serverResponse: ServerResponseType) => {
  const principalInsured = getPrincipalInsured(serverResponse);
  const contactInformation = principalInsured!.attributes.contactInformation
    .attributes;
  return {
    [principalInsured!.attributes.identifier]: {
      email: mapToClientDataType(contactInformation.email),
      phoneNumber: mapToClientDataType(
        contactInformation.telephone.attributes.number,
        value => (value ? formatPhoneNumber(value) : '')
      ),
      phoneType: mapToClientDataType(
        contactInformation.telephone.attributes.phoneType
      ),
    },
  };
};

export const transformAgencyInformation = (
  autoInsuranceProduct: InsuranceProductType
): AgencyInformationType => {
  const agencyInformation = {
    ...autoInsuranceProduct.attributes.agencyInformation,
  };
  return agencyInformation;
};

export const transformEligibleDiscounts = (
  autoInsuranceProduct: InsuranceProductType
) => {
  return autoInsuranceProduct?.attributes.eligibleDiscounts || [];
};

export const transformPolicyHolders = (
  autoInsuranceProduct?: InsuranceProductType
) => {
  return autoInsuranceProduct?.attributes.policyHolders || [];
};

export const transformProductType = (
  autoInsuranceProduct?: InsuranceProductType
) => autoInsuranceProduct?.attributes.productType;

export const transformProductSegment = (
  autoInsuranceProduct?: InsuranceProductType
) => autoInsuranceProduct?.attributes.productSegment;

export const transformEffectiveDate = (
  serverResponse: ServerResponseType,
  attribureName: 'effectiveDate' | 'terminationDate'
) => {
  const insuranceProduct = serverResponse.insuranceApplication.attributes.insuranceProducts.find(
    (product: InsuranceProductType) =>
      product.attributes.productType === 'personalAuto'
  );

  return mapToClientDataType(
    insuranceProduct?.attributes.policyTerm.attributes[attribureName],
    value => (value ? formatDateShort(value) : '')
  );
};

export const transformTelematics = (seerverResponse: ServerResponseType) => {
  const insuranceProduct = seerverResponse.insuranceApplication.attributes.insuranceProducts.find(
    (product: InsuranceProductType) =>
      product.attributes.productType === 'personalAuto'
  );

  return insuranceProduct?.attributes.telematics;
};

export const transformQuoteNumber = (
  serverResponse: ServerResponseType
): string => serverResponse.insuranceApplication.attributes.quoteNumber;

const transformCommand = (
  command: CommandType | undefined
): Partial<CommandType> => ({
  status: command?.status || 'not_runnable',
  runnable: isPresent(command) ? command.runnable : false,
});

const transformCommands = (serverResponse: ServerResponseType) => {
  const bindCommand = serverResponse.insuranceApplication.commands.find(
    c => c.name === 'bind'
  );

  const sendSignaturePacketCommand = serverResponse.insuranceApplication.commands.find(
    c => c.name === 'send_signature_packet'
  );

  const completeUnderwritingConsentCommand = serverResponse.insuranceApplication.commands.find(
    c => c.name === 'complete_underwriting_consent'
  );

  const unacceptUnderwritingConsentCommand = serverResponse.insuranceApplication.commands.find(
    c => c.name === 'unaccept_underwriting_consent'
  );

  const confirmAddressesMatchCommand = serverResponse.insuranceApplication.commands.find(
    c => c.name === 'confirm_mailing_address_matches_residency_address'
  );

  const autoInsuranceProduct = getAutoInsuranceProduct(serverResponse);
  return {
    reports: serverResponse.insuranceApplication.attributes.reports.map(c => ({
      reportName: c.reportEnum,
      runnable: c.command.runnable,
      status: c.command.status,
    })),
    bind: transformCommand(bindCommand),
    sendSignaturePacket: sendSignaturePacketCommand
      ? transformCommand(sendSignaturePacketCommand)
      : undefined,
    addresses: serverResponse.insuranceApplication.attributes.addresses.reduce(
      (acc: AddressType['commands'], address) => [...acc, ...address.commands],
      []
    ),
    confirmAddressesMatch: confirmAddressesMatchCommand
      ? transformCommand(confirmAddressesMatchCommand)
      : undefined,
    completeUnderwritingConsent: completeUnderwritingConsentCommand
      ? transformCommand(completeUnderwritingConsentCommand)
      : undefined,
    unacceptUnderwritingConsent: completeUnderwritingConsentCommand
      ? transformCommand(unacceptUnderwritingConsentCommand)
      : undefined,
    insuranceProduct: autoInsuranceProduct?.commands || [],
    drivers: serverResponse.insuranceApplication.attributes.insurableParties.reduce(
      (acc: DriverCommandsType, driver) => {
        return {
          ...acc,
          [driver.attributes.identifier]: {
            commands: driver.commands ?? driver.attributes.commands,
            challenging: {
              currentInsurance: driver.attributes.currentInsurance.commands,
              driverLicense: driver.attributes.driverLicense.commands,
            },
            accidents: driver.attributes.accidents.reduce(
              (acc: DriverCommandsType[0]['accidents'], accident) => {
                return {
                  ...acc,
                  [accident.attributes.identifier]: accident.commands,
                };
              },
              {}
            ),
            violations: driver.attributes.violations.reduce(
              (acc: DriverCommandsType[0]['violations'], violation) => {
                return {
                  ...acc,
                  [violation.attributes.identifier]: violation.commands,
                };
              },
              {}
            ),
          },
        };
      },
      {}
    ),
    vehicles: serverResponse.insuranceApplication.attributes.insurableItems.reduce(
      (acc: VehicleCommandsType, vehicle) => {
        return {
          ...acc,
          [vehicle.attributes.identifier]: { commands: vehicle.commands },
        };
      },
      {}
    ),
  };
};

const transformAddressDetails = (
  serverResponse: ServerResponseType,
  addressType: AddressCategoryType
) => {
  const firstAddress =
    serverResponse.insuranceApplication.attributes.addresses[0];

  const residencyAddress = serverResponse.insuranceApplication.attributes.addresses.find(
    address => address.attributes.addressType.value === 'residency'
  );
  const mailingAddress = serverResponse.insuranceApplication.attributes.addresses.find(
    address => address.attributes.addressType.value === 'mailing'
  );

  const principalInsured = getPrincipalInsured(serverResponse);

  function mapAddressEntityToClient(
    address: AddressType,
    addressCategory: AddressCategoryType
  ): AddressDataType {
    const a = address.attributes;

    return {
      identifier: a.identifier,
      addressValidationOutcome: mapToClientDataType(a.addressValidationOutcome),
      address1: mapToClientDataType(a.addressLine1),
      address2: mapToClientDataType(a.addressLine2),
      city: mapToClientDataType(a.city),
      state: mapToClientDataType(a.state),
      zip: mapToClientDataType(a.postalCode),
      type: addressCategory,
    };
  }

  if (residencyAddress && addressType === 'residency') {
    return {
      [principalInsured!.attributes.identifier]: mapAddressEntityToClient(
        residencyAddress,
        'residency'
      ),
    };
  }

  if (mailingAddress && addressType === 'mailing') {
    return {
      [principalInsured!.attributes.identifier]: mapAddressEntityToClient(
        mailingAddress,
        'mailing'
      ),
    };
  }

  // If no residency address is present, we assume it is mailing address
  if (!residencyAddress && addressType === 'residency') {
    if (mailingAddress) {
      return {
        [principalInsured!.attributes.identifier]: mapAddressEntityToClient(
          mailingAddress,
          'residency'
        ),
      };
    }
  }

  // If no mailing address is present, we assume it is residency address
  if (!mailingAddress && addressType === 'mailing') {
    if (residencyAddress) {
      return {
        [principalInsured!.attributes.identifier]: mapAddressEntityToClient(
          residencyAddress,
          'mailing'
        ),
      };
    }
  }

  // If neither address is present, we assume the first address is both.
  return {
    [principalInsured!.attributes.identifier]: mapAddressEntityToClient(
      firstAddress,
      addressType
    ),
  };
};

export const isInsuranceProductResponse = (
  value: InsuranceProductType | ServerErrorResponseType
): value is InsuranceProductType => {
  return value.hasOwnProperty('attributes');
};

const transformCoverageDetails = (serverResponse: ServerResponseType) => {
  const insuranceProduct = serverResponse.insuranceApplication.attributes.insuranceProducts.find(
    (product: InsuranceProductType) =>
      product.attributes.productType === 'personalAuto'
  );

  return transformCoverages(insuranceProduct);
};

export const transformCoverages = (
  serverResponse?: InsuranceProductType | ServerErrorResponseType | undefined
) => {
  if (!serverResponse || isInsuranceProductResponse(serverResponse)) {
    const policyCoverages =
      serverResponse?.attributes.coverages.insuranceProduct ?? [];
    const vehicleCoverages =
      serverResponse?.attributes.coverages.insurableItem ?? [];

    return {
      policy: policyCoverages.reduce<
        Record<string, Required<ClientFieldDataType>>
      >(
        (acc, c) => ({
          ...acc,
          [c.attributes.coverageType]: mapToClientDataType({
            ...c.attributes.coverageLevelEnum,
            errors: [...c.errors, ...c.attributes.coverageLevelEnum.errors], // Mix of schema validation errors and healing messages
          }),
        }),
        {}
      ),
      vehicles: vehicleCoverages.reduce<
        Record<string, Record<string, Required<ClientFieldDataType>>>
      >(
        (acc, c) => ({
          ...acc,
          [c.attributes.insurableItemIdentifier]: {
            ...(acc[c.attributes.insurableItemIdentifier] ?? {}),
            [c.attributes.coverageType]: mapToClientDataType({
              ...c.attributes.coverageLevelEnum,
              errors: [...c.errors, ...c.attributes.coverageLevelEnum.errors], // Mix of schema validation errors and healing messages
            }),
          },
        }),
        {}
      ),
    };
  } else {
    // server returned an error, so we throw an error and the consumer handles these errors.
    throw new Error(serverResponse.message);
  }
};

type ValueTransformerType<T> = (value: T) => ClientFieldDataType['value'];

const mapToClientDataType = function<T extends Object>(
  fieldData: FieldDataType<T> | undefined,
  valueTransformer:
    | ValueTransformerType<FieldDataType<T>['value']>
    | undefined = undefined,
  editableIfEmpty = false
): ClientFieldDataType {
  if (!fieldData) {
    return {
      value: '',
      errors: [],
      source: '',
      editable: editableIfEmpty,
      masked: false,
    };
  }

  let value;

  if (valueTransformer) {
    value = valueTransformer(fieldData.value);
  } else if (
    typeof fieldData.value !== 'undefined' &&
    fieldData.value !== null
  ) {
    value = fieldData.value.toString();
  } else {
    value = '';
  }

  return {
    value,
    errors: fieldData.errors,
    source: fieldData.source?.toString() ?? '',
    editable: fieldData.editable,
    masked: fieldData.masked,
  };
};

const getAutoInsuranceProduct = (serverResponse: ServerResponseType) =>
  serverResponse.insuranceApplication.attributes.insuranceProducts.find(
    (product: InsuranceProductType) =>
      product.attributes.productType === 'personalAuto'
  );

const getInsurableProductPolicyHolders = (
  serverResponse: ServerResponseType
) => {
  return (
    getAutoInsuranceProduct(serverResponse)?.attributes.policyHolders ?? []
  );
};

const transformDriverDetails = (serverResponse: ServerResponseType) => {
  const insuranceProductPolicyHolders = getInsurableProductPolicyHolders(
    serverResponse
  );

  return serverResponse.insuranceApplication.attributes.insurableParties.reduce(
    (
      acc: DriversDataType,
      {
        attributes: {
          identifier,
          editable,
          discoveredDriver,
          accidents,
          coverageHistory,
          personalInfo,
          driverLicense,
          violations,
          drivingHistory,
          drivingCourse,
          currentInsurance,
          contactInformation,
          education,
          excludeFromQuote,
          exclusionReason,
          homeOwnership,
          militaryService,
          profession,
          isGoodStudent,
          statusDetails,
          stateResidency,
          selectiveMvrOrdering,
          cfr,
        },
      }: InsurablePartyType
    ) => {
      return {
        ...acc,
        [identifier]: {
          identifier: identifier,
          editable: editable,
          accidents: accidents.reduce(
            (accidentAcc, accident) => ({
              ...accidentAcc,
              [accident.attributes.identifier]: {
                identifier: accident.attributes.identifier,
                atFault: mapToClientDataType(accident.attributes.atFault),
                day: mapToClientDataType(accident.attributes.day),
                month: mapToClientDataType(accident.attributes.month),
                year: mapToClientDataType(accident.attributes.year),
                lossType: mapToClientDataType(accident.attributes.lossType),
                accidentType: mapToClientDataType(
                  accident.attributes.accidentType
                ),
                typeOfLoss: mapToClientDataType(accident.attributes.typeOfLoss),
                isLendingLoss: accident.attributes.isLendingLoss,
                date: mapToClientDataType(accident.attributes.date),
                lossAmount: mapToClientDataType(accident.attributes.lossAmount),
                claimStatus: mapToClientDataType(
                  accident.attributes.claimStatus
                ),
              },
            }),
            {}
          ),
          ageFirstLicensed: mapToClientDataType(
            drivingHistory.attributes.ageFirstLicensed
          ),
          coveredWithinLookbackPeriod: mapToClientDataType(
            coverageHistory?.attributes.coveredWithinLookbackPeriod
          ),
          currentInsuranceBiLevel: mapToClientDataType(
            currentInsurance.attributes?.biLevel
          ),
          currentInsuranceCarrierName: mapToClientDataType(
            currentInsurance.attributes?.carrierName
          ),
          currentInsuranceLapseDuration: mapToClientDataType(
            currentInsurance.attributes?.lapseDuration
          ),
          currentInsuranceLapseReason: mapToClientDataType(
            currentInsurance.attributes?.lapseReason
          ),
          currentInsuranceLapseReasonOther: mapToClientDataType(
            currentInsurance.attributes?.lapseReasonOther
          ),
          currentInsuranceInsuredDuration: mapToClientDataType(
            currentInsurance.attributes?.insuredDuration
          ),
          dateOfBirth: mapToClientDataType(
            personalInfo.attributes.dateOfBirth,
            value => {
              if (!value) return '';
              return value.match(ISOMaskedRegex)
                ? parseFromMaskedISO(value)
                : formatDateShort(value);
            }
          ),
          discoveredDriver: discoveredDriver.value ?? false,
          driverType: getDriverType(insuranceProductPolicyHolders, identifier),
          drivingCourseDate: mapToClientDataType(
            drivingCourse.attributes.completionDate,
            value => (value ? formatDateShort(value) : '')
          ),
          educationLevel: mapToClientDataType(
            education.attributes.currentLevel
          ),
          email: mapToClientDataType(contactInformation.attributes.email),
          excludeFromQuote: mapToClientDataType(excludeFromQuote),
          exclusionReason: mapToClientDataType(exclusionReason),
          firstName: mapToClientDataType(personalInfo.attributes.firstName),
          gender: mapToClientDataType(personalInfo.attributes.gender),
          hasCompletedDrivingCourse: mapToClientDataType(
            drivingCourse.attributes.courseTaken
          ),
          isCurrentlyInsured: mapToClientDataType(
            currentInsurance.attributes?.insured
          ),
          isDeployed: mapToClientDataType(
            militaryService.attributes.isDeployed
          ),
          isHomeOwner: mapToClientDataType(homeOwnership.attributes.homeOwner),
          lastName: mapToClientDataType(personalInfo.attributes.lastName),
          licenseNumber: mapToClientDataType(driverLicense.attributes.number),
          licensePreviouslySuspended: mapToClientDataType(
            drivingHistory.attributes.licensePreviouslySuspended
          ),
          licenseState: mapToClientDataType(driverLicense.attributes.state),
          licenseStatus: mapToClientDataType(driverLicense.attributes.status),
          licenseVerified: mapToClientDataType(
            driverLicense.attributes.verified
          ),
          hasForeignLicense: mapToClientDataType(
            driverLicense.attributes.hasForeignLicense
          ),
          hasLearnersPermit: mapToClientDataType(
            driverLicense.attributes.hasLearnersPermit
          ),
          maritalStatus: mapToClientDataType(
            personalInfo.attributes.maritalStatus
          ),
          middleName: mapToClientDataType(personalInfo.attributes.middleName),
          militaryRank: mapToClientDataType(
            militaryService.attributes.militaryRank
          ),
          militaryServiceType: mapToClientDataType(
            militaryService.attributes.serviceType
          ),
          phoneNumber: mapToClientDataType(
            contactInformation.attributes.telephone.attributes.number
          ),
          phoneType: mapToClientDataType(
            contactInformation.attributes.telephone.attributes.phoneType
          ),
          profession: mapToClientDataType(profession),
          previousSuspensionCount: mapToClientDataType(
            drivingHistory.attributes.previousSuspensionCount
          ),
          previousSuspensionDate: mapToClientDataType(
            drivingHistory.attributes.previousSuspensionDate
          ),
          relationshipToApplicant: mapToClientDataType(
            personalInfo.attributes.relationshipToApplicant
          ),
          ssn: mapToClientDataType(personalInfo.attributes.ssn),
          declinedSsn: personalInfo.attributes.declinedSsn?.value ?? false,
          suffix: mapToClientDataType(personalInfo.attributes.suffix),
          isGoodStudent: mapToClientDataType(isGoodStudent),
          hasAccessToVehicles: mapToClientDataType(
            statusDetails?.attributes?.hasAccessToVehicles
          ),
          livesInHousehold: mapToClientDataType(
            statusDetails?.attributes?.livesInHousehold
          ),
          hasOwnInsurancePolicy: mapToClientDataType(
            statusDetails?.attributes?.hasOwnInsurancePolicy
          ),
          violations: violations.reduce(
            (violationAcc, violation) => ({
              ...violationAcc,
              [violation.attributes.identifier]: {
                identifier: violation.attributes.identifier,
                month: mapToClientDataType(violation.attributes.month),
                day: mapToClientDataType(violation.attributes.day),
                violationType: mapToClientDataType(
                  violation.attributes.violationType
                ),
                year: mapToClientDataType(violation.attributes.year),
                sr22: mapToClientDataType(violation.attributes.sr22),
              },
            }),
            {}
          ),
          inStateResidencyDuration: mapToClientDataType(
            stateResidency.attributes.inStateResidencyDuration
          ),
          multipleHouseholdsMultipleStates: mapToClientDataType(
            stateResidency.attributes.multipleHouseholdsMultipleStates
          ),
          outOfStateStudent: mapToClientDataType(
            stateResidency.attributes.outOfStateStudent
          ),
          ownVehicleWhileOutOfStateStudent: mapToClientDataType(
            stateResidency.attributes.ownVehicleWhileOutOfStateStudent
          ),
          selectiveMvrOrdering: mapToClientDataType(selectiveMvrOrdering),
          cfrRequired: mapToClientDataType(cfr?.attributes?.required),
          cfrCaseNumber: mapToClientDataType(cfr?.attributes?.caseNumber),
          cfrType: mapToClientDataType(cfr?.attributes?.cfrType),
          cfrIssueState: mapToClientDataType(cfr?.attributes?.issueState),
          userReportedStatus: mapToClientDataType(
            driverLicense.attributes.userReportedStatus
          ),
          licenseReportStatus: mapToClientDataType(
            driverLicense.attributes.reportStatus
          ),
        },
      };
    },
    {}
  );
};

const transformVehicleDetails = (serverResponse: ServerResponseType) =>
  serverResponse.insuranceApplication.attributes.insurableItems.reduce(
    (acc: VehiclesDataType, vehicle: InsurableItemType) => {
      const {
        adas,
        needsAdas,
        identifier,
        editable,
        ownership,
        parkingDetails,
        parkingType,
        usageDetails,
        antiTheft,
        vehicleIdentification,
        mileage,
        usage,
        rideshareUse,
        needsGaragingAddress,
        garagingAddress,
        garagedAtPrimaryAddress,
        businessUse,
        businessUseType,
      } = vehicle.attributes;
      return {
        ...acc,
        [identifier]: {
          adas,
          needsAdas,
          identifier,
          editable,
          activeDisable: mapToClientDataType(
            antiTheft.attributes.activeDisable,
            value => (value ? value.toString() : DEFAULTS.vehicle.activeDisable)
          ),
          alarm: mapToClientDataType(antiTheft.attributes.alarm, value =>
            value ? value.toString() : DEFAULTS.vehicle.alarm
          ),
          annualMileage: mapToClientDataType(mileage.attributes.annual),
          bodyStyle: mapToClientDataType(
            vehicleIdentification.attributes.bodyStyle
          ),
          lienHolder: {
            name: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.name,
              undefined,
              true
            ),
            addressLine1: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.address.attributes
                .addressLine1,
              undefined,
              true
            ),
            addressLine2: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.address.attributes
                .addressLine2,
              undefined,
              true
            ),
            city: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.address.attributes
                .city,
              undefined,
              true
            ),
            state: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.address.attributes
                .state,
              undefined,
              true
            ),
            postalCode: mapToClientDataType(
              ownership.attributes.lienholders[0]?.attributes.address.attributes
                .postalCode,
              undefined,
              true
            ),
          },
          make: mapToClientDataType(vehicleIdentification.attributes.make),
          model: mapToClientDataType(vehicleIdentification.attributes.model),
          ownershipType: mapToClientDataType(
            ownership.attributes.ownershipType
          ),
          parkingDetails: {
            inStateParkingDuration: mapToClientDataType(
              parkingDetails.attributes.inStateParkingDuration
            ),
            parkingLocationReason: mapToClientDataType(
              parkingDetails.attributes.parkingLocationReason
            ),
          },
          parkingType: mapToClientDataType(parkingType ?? 'unknown'),
          usedInMexico: mapToClientDataType(
            usageDetails.attributes.usedInMexico,
            convertToString
          ),
          availableForNonCoveredDrivers: mapToClientDataType(
            usageDetails.attributes.availableForNonCoveredDrivers,
            convertToString
          ),
          emergencyServices: mapToClientDataType(
            usageDetails.attributes.emergencyServices,
            convertToString
          ),
          feeBasedServices: mapToClientDataType(
            usageDetails.attributes.feeBasedServices,
            convertToString
          ),
          hazmatToolTransport: mapToClientDataType(
            usageDetails.attributes.hazmatToolTransport,
            convertToString
          ),
          usedAsResidence: mapToClientDataType(
            usageDetails.attributes.usedAsResidence,
            convertToString
          ),
          passiveDisable: mapToClientDataType(
            antiTheft.attributes.passiveDisable,
            value =>
              value ? value.toString() : DEFAULTS.vehicle.passiveDisable
          ),
          recovery: mapToClientDataType(antiTheft.attributes.recovery, value =>
            value ? value.toString() : DEFAULTS.vehicle.recovery
          ),
          rideshareUse: mapToClientDataType(rideshareUse),
          registeredToApplicant: mapToClientDataType(
            ownership.attributes.registeredToApplicant
          ),
          totalMileage: mapToClientDataType(mileage.attributes.total),
          usage: mapToClientDataType(usage),
          vehicleServicesId: mapToClientDataType(
            vehicleIdentification.attributes.vehicleServicesId
          ),
          partialVin: mapToClientDataType(
            vehicleIdentification.attributes.partialVin
          ),
          vin: mapToClientDataType(vehicleIdentification.attributes.vin),
          year: mapToClientDataType(vehicleIdentification.attributes.year),
          needsGaragingAddress,
          garagingAddress: {
            addressLine1: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes?.addressLine1,
              undefined,
              true
            ),
            addressLine2: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes?.addressLine2,
              undefined,
              true
            ),
            city: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes?.city,
              undefined,
              true
            ),
            state: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes?.state,
              undefined,
              true
            ),
            postalCode: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes?.postalCode,
              undefined,
              true
            ),
            garagingAddressOutcome: mapToClientDataType(
              garagingAddress?.attributes?.address?.attributes
                ?.addressValidationOutcome,
              undefined,
              true
            ),
          },
          garagingAddressValidation: transformGaragingAddressOutcomes(
            garagingAddress
          ),
          garagedAtPrimaryAddress: mapToClientDataType(garagedAtPrimaryAddress),
          hasAntiTheftDevice: mapToClientDataType(
            antiTheft.attributes.hasAntiTheftDevice
          ),
          businessUse: mapToClientDataType(businessUse),
          businessUseType: mapToClientDataType(businessUseType),
        },
      };
    },
    {}
  );

const transformUnderwritingDetails = (serverResponse: ServerResponseType) => {
  return {
    failureReasons:
      serverResponse.insuranceApplication.attributes.underwriting
        .failureReasons ?? [],
    underwritingStops:
      serverResponse.insuranceApplication.attributes.underwriting
        .underwritingStops ?? [],
    underwritingPassed:
      serverResponse.insuranceApplication.attributes.underwriting
        .underwritingPassed ?? true, // if undefined, this can default to true since there is simply no decision supplied yet
  };
};

const transformUnderwritingConsent = (
  serverResponse: ServerResponseType
): GetDetailsResponseType['underwritingConsent'] => {
  return {
    givenAt:
      serverResponse.insuranceApplication.attributes?.underwritingConsent
        ?.givenAt ?? null,
    markdown:
      serverResponse.insuranceApplication.attributes?.underwritingConsent
        ?.markdown ?? null,
  };
};

export const transformStatus = (serverResponse: ServerResponseType) => {
  return serverResponse.insuranceApplication.attributes.status;
};

const transformDocumentSignatures = (serverResponse: ServerResponseType) => {
  return (
    serverResponse.insuranceApplication.attributes.documentSignatures || []
  );
};

const transformDisclosures = (serverResponse: ServerResponseType) => {
  return serverResponse.insuranceApplication.attributes.disclosures || [];
};

const allowedOutcomes = ['mailable', 'unavailable', 'vacant', 'overridden'];

export const transformGaragingAddressOutcomes = (
  garagingAddress: InsurableItemType['attributes']['garagingAddress']
) => {
  const garagingAddressOutcome =
    garagingAddress.attributes.address.attributes.addressValidationOutcome;

  const garagingAddressLine1 =
    garagingAddress.attributes.address.attributes.addressLine1;

  const outcome = garagingAddressOutcome?.value || null;
  const hasOutcomeErrors = garagingAddressOutcome?.errors.length > 0;

  const isAddressValid = allowedOutcomes.includes(outcome || '');
  const isMissingLineTwo = outcome === 'missing_line_two';

  if (
    outcome === null &&
    (!hasOutcomeErrors || garagingAddressLine1.value === null)
  ) {
    // We don't have an address or an outcome yet, so don't show any error
    return null;
  }

  return {
    isAddressValid,
    isMissingLineTwo,
  };
};

const transformSoftStops = (serverResponse: ServerResponseType) => {
  return serverResponse.insuranceApplication.attributes.softStops ?? [];
};

export const transformPriorPolicies = (
  serverResponse: ServerResponseType
): Record<string, PriorPolicyResponseType[]> => {
  const parties =
    serverResponse.insuranceApplication?.attributes.insurableParties ?? [];

  const result: Record<string, PriorPolicyResponseType[]> = {};

  parties.forEach(party => {
    const driverId = party.attributes.identifier;
    const policiesOrUndefined = party.attributes.priorPolicies;

    result[driverId] = Array.isArray(policiesOrUndefined)
      ? policiesOrUndefined
      : [];
  });

  return result;
};
